import React from "react";
import PropTypes from "prop-types";
import { RichDataTable } from "../../../../components/table/RichDataTable";
import { Column } from "primereact/column";
import {
  PageHeight40,
  PageTitle,
  createSortByAttr,
  scrollToTop,
  toUIDate,
} from "../../../../components/Utils";
import { FormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import JarvisConnectionTaskForm from "../../components/itConnections/JarvisConnectionTaskForm";
import JarvisConnectionTaskMenu from "../../components/itConnections/JarvisConnectionTaskMenu";

import { connect } from "react-redux";
import {
  setSelectedJarvisConnections,
  exportJarvisConnections,
  filterJarvisConnections,
  closeConnectionTasks,
  closeConnectionTask,
  deleteConnectionTask,
} from "../../actions";
import {
  columnStyle,
  filterTasksByTaskType,
  getTaskTypeLabel,
  isDeletionTask,
  renderNgumUserIDColumn,
  renderRequesterColumn,
  renderUserNameColumn,
  actionColumnStyle,
} from "../../components/Utils";
const CONNECTION_TYPE = "Jarvis";

const mapStateToProps = (state) => {
  return {
    isLoading: state.task.loading,
    data: state.task.jarvisConnections,
    selection: state.task.selectedJarvisConnections,
    connectionTaskType: state.task.currentJarvisConnectionType,
  };
};
const mapDispatchToProps = (dispatch) => ({
  setSelection: (...args) => dispatch(setSelectedJarvisConnections(...args)),
  exportJarvisConnections: (...args) =>
    dispatch(exportJarvisConnections(...args)),
  filterConnections: (...args) => dispatch(filterJarvisConnections(...args)),
  closeJarvisConnections: (...args) => dispatch(closeConnectionTasks(...args)),
  saveTask: (...args) => dispatch(closeConnectionTask(...args)),
  deleteTask: (...args) => dispatch(deleteConnectionTask(...args)),
});

function JarvisConnectionTaskList({
  className = "",
  isLoading,
  rows = 10,
  data,
  selection,
  setSelection,
  exportJarvisConnections,
  closeJarvisConnections,
  filterConnections,
  connectionTaskType,
  saveTask,
  deleteTask,
}) {
  const formRef = React.useRef(null);

  React.useEffect(() => {
    scrollToTop();
  }, []);

  const onSave = React.useCallback(
    (onHide) => {
      const task = formRef.current.getModel();
      if (!task) return;
      console.log(`>> saving ${CONNECTION_TYPE} connection task...`, task);
      saveTask({ connectionType: CONNECTION_TYPE, task }, onHide);
    },
    [saveTask],
  );

  const onDelete = React.useCallback(
    (task) => {
      console.log(`${CONNECTION_TYPE}ConnectionTaskList.onDelete()`, task);
      deleteTask({ connectionType: CONNECTION_TYPE, task });
    },
    [deleteTask],
  );

  const onCloseTasks = React.useCallback(
    (tasks) => {
      console.log("closing tasks", tasks);
      closeJarvisConnections({ connectionType: CONNECTION_TYPE, tasks });
    },
    [closeJarvisConnections],
  );

  const isDeleting = React.useMemo(
    () => isDeletionTask(connectionTaskType),
    [connectionTaskType],
  );

  const renderItemTable = React.useCallback(
    (items, actionTemplate) => {
      const taskItems = items
        .map((item) => {
          if (!item.user) {
            console.log(
              "JarvisConnectionTaskList.renderItemTable() item.user is empty",
              item,
            );
          }
          return {
            ...item,
            user: (item.user || "").split(",").join(" "),
            requester: `${item.requester || ""}`.split(",").join(" "),
            requestDateUI: toUIDate(
              item.request_date,
              "YYYY-MM-DDTHH:mm:ss:SSSZ",
            ),
            requestDate: toUIDate(
              item.request_date,
              "YYYY-MM-DDTHH:mm:ss:SSSZ",
              "YYYY.MM.DD",
            ),
            userInCharge: `${item.userInCharge || ""}`.split(",").join(" "),
          };
        })
        .sort(createSortByAttr("request_date", true, true));
      return (
        <RichDataTable
          paginator
          paginatorLeftProps={{
            emptyText: "Keine JARVIS-Zugänge",
            itemName: "JARVIS-Zugang",
            itemNamePlural: "JARVIS-Zugänge"
          }}
          data={taskItems}
          rows={rows}
          selection={selection}
          setSelection={setSelection}
          removableSort
          sortMode="multiple"
          isLoading={isLoading}
          connectionTaskType={connectionTaskType}
        >
          <Column selectionMode="multiple" style={{ width: "3em" }} />
          <Column
            field="parentCompanyName"
            header="Vertragspartner"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
          />
          <Column
            field="user"
            header="Mitarbeiter"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle, width: "13em" }}
            body={renderUserNameColumn}
          />
          <Column
            field="ngumUserId"
            header="nGUM UserID"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle }}
            body={renderNgumUserIDColumn}
          />
          <Column
            field="requester"
            header="Beantrager"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle, width: "12em" }}
            body={renderRequesterColumn}
          />
          <Column
            field="requestDate"
            header="Erstelldatum"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle, width: "10em", textAlign: "center" }}
            body={(item) => item.requestDateUI}
          />
          <Column
            field="status"
            header="Status"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle, textAlign: "center", width: "9em" }}
          />
          <Column
            body={actionTemplate}
            style={{ ...actionColumnStyle, textAlign: "center", width: "7em" }}
          />
        </RichDataTable>
      );
    },
    [rows, selection, setSelection, isLoading, connectionTaskType],
  );

  const renderItemForm = React.useCallback(
    (item, onHide) => {
      const connection = item;
      return (
        <div>
          <JarvisConnectionTaskForm
            ref={formRef}
            model={connection}
            isDeleting={isDeleting}
          />
          <FormMenu
            className="margin-big-top w3-border-top pad-big"
            style={{
              marginLeft: "-1em",
              marginRight: "-1em",
              marginBottom: "-1em",
            }}
            onSave={() => onSave(onHide)}
            onCancel={onHide}
            saveBtnName="saveJarvisConnectionBtn"
            saveBtnLabel={isDeleting ? "Bestätigen" : undefined}
          />
        </div>
      );
    },
    [isDeleting, onSave],
  );

  const getItemFormTitle = React.useCallback((_connection) => {
    let title = "JARVIS-Zugang bearbeiten";
    if (isDeleting) {
      title = "JARVIS-Zugangslöschung bestätigen";
    }
    return <h3 className="no-margin">{title}</h3>;
  }, []);

  const title = (
    <PageTitle>
      <JarvisConnectionTaskMenu
        className="w3-right"
        selectedJarvisConnections={selection}
        exportJarvisConnections={exportJarvisConnections}
        closeJarvisConnections={onCloseTasks}
        filterConnections={filterConnections}
        connectionTaskType={connectionTaskType}
        items={data}
      />
      <h3 className="no-margin">{"JARVIS-Zugänge"}</h3>
    </PageTitle>
  );

  return (
    <PageHeight40 className={className}>
      <ItemListManager
        key={Math.random()}
        itemPanelStyle={{ width: "480px" }}
        title={title}
        addBtnHidden
        items={data.filter((item) =>
          filterTasksByTaskType(item, connectionTaskType),
        )}
        getItemFormTitle={getItemFormTitle}
        renderItemTable={renderItemTable}
        renderItemForm={renderItemForm}
        namePrefix={`${CONNECTION_TYPE}Connection`}
        itemDeleteable
        // deleteTitle={`${CONNECTION_TYPE}-Zugangsaufgabe löschen`}
        deleteTitle={`JARVIS-Zugangsaufgabe löschen`}
        getDeleteText={(_item) => {
          const taskLabel = getTaskTypeLabel(connectionTaskType);
          return `Diese ${taskLabel} wird nun rückgängig gemacht. Wollen Sie diese Aufgabe unwiderruflich löschen?`;
        }}
        onDelete={onDelete}
      />
    </PageHeight40>
  );
}
JarvisConnectionTaskList.propTypes = {
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  selection: PropTypes.array,
  connectionTaskType: PropTypes.string,
  exportJarvisConnections: PropTypes.func.isRequired,
  filterConnections: PropTypes.func.isRequired,
  closeJarvisConnections: PropTypes.func.isRequired,
  saveTask: PropTypes.func.isRequired,
  deleteTask: PropTypes.func.isRequired,
  rows: PropTypes.number,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(JarvisConnectionTaskList);
